import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type {
  HappySession,
  HappyMessage,
  AgentActivity,
  AgentStats,
  ConnectionState,
  AuthState,
  CommandStatus,
  CommandExecution,
  CommandType,
  HookEvent,
  SessionOutputEventType,
  ToolType,
} from '../types/happy';
import type { ToolCallEntry, ToolCallAggregationState, AgentInfo } from '../hooks/useToolCallAggregation';
import { generateSessionTitle, generateAiSessionTitle, hasTopicChanged } from '../utils/sessionTitle';
// NOTE: happyPersistence has been refactored to cloud-only (no localStorage)
// loadFromStorage() and saveToStorage() are now no-ops
import { happyPersistence } from '../services/happyPersistence';

// ============================================
// Client-Side Message Deduplication System
// ============================================
// Multi-layer deduplication to catch duplicates that slip past the server:
// 1. FAST PATH: Set-based message ID tracking (O(1) lookup)
// 2. SLOW PATH: Array iteration for content-based matching
// 3. TIMESTAMP: 10-second window for content matches
//
// This is the LAST LINE OF DEFENSE after server-side deduplication.
// ============================================

/**
 * Track recently received message IDs for fast duplicate detection.
 * Uses a Set for O(1) lookup performance.
 * Auto-clears entries after TTL to prevent memory bloat.
 *
 * @remarks
 * This is the FAST PATH check - happens before any array iteration.
 * Works in conjunction with server-side deduplication (Phase 1).
 */
const recentMessageIds = new Set<string>();

// ============================================
// DEDUP-017: Active Streaming Message Tracking
// ============================================
// Track active streaming messages per session to:
// 1. Prevent double finalization
// 2. Detect stale streams (timeout detection)
// 3. Provide debug visibility into streaming state
// ============================================

/**
 * State tracking for an active streaming message
 */
export interface StreamingState {
  /** The message ID being streamed */
  messageId: string;
  /** When streaming started (timestamp) */
  startedAt: number;
  /** When the last chunk was received */
  lastChunkAt: number;
  /** Accumulated content length in characters */
  accumulatedLength: number;
}

/**
 * Track active streaming messages per session.
 * Key: sessionId, Value: StreamingState
 *
 * This is module-level state (not in Zustand) because:
 * 1. It needs to be accessed from socket event handlers
 * 2. It's transient state that shouldn't persist
 * 3. Performance: direct Map access is faster than store updates
 */
const activeStreams = new Map<string, StreamingState>();

/**
 * Stream timeout threshold in milliseconds.
 * If no chunks received for this duration, stream is considered stale.
 */
const STREAM_TIMEOUT_MS = 30000; // 30 seconds

/**
 * Interval for checking stale streams (milliseconds)
 */
const STALE_CHECK_INTERVAL_MS = 10000; // 10 seconds

/**
 * Start tracking a new streaming message for a session.
 * Called when message:stream arrives with isFirst: true
 *
 * @param sessionId - The session receiving the stream
 * @param messageId - The message ID being streamed
 */
export function startStreamTracking(sessionId: string, messageId: string): void {
  const now = Date.now();
  activeStreams.set(sessionId, {
    messageId,
    startedAt: now,
    lastChunkAt: now,
    accumulatedLength: 0,
  });
  console.log(`[HappyStore] Stream started: ${messageId} in session ${sessionId}`);
}

/**
 * Update stream tracking when a new chunk arrives.
 * Called on each message:stream event (except first).
 *
 * @param sessionId - The session receiving the stream
 * @param contentLength - Length of the new content chunk
 */
export function updateStreamTracking(sessionId: string, contentLength: number): void {
  const state = activeStreams.get(sessionId);
  if (state) {
    state.lastChunkAt = Date.now();
    state.accumulatedLength += contentLength;
  }
}

/**
 * Get the active stream state for a session.
 *
 * @param sessionId - The session to check
 * @returns The streaming state or undefined if no active stream
 */
export function getActiveStream(sessionId: string): StreamingState | undefined {
  return activeStreams.get(sessionId);
}

/**
 * Clear the active stream for a session.
 * Called when stream is finalized via message:new or timeout.
 *
 * @param sessionId - The session to clear
 * @returns true if a stream was cleared, false if none existed
 */
export function clearActiveStream(sessionId: string): boolean {
  const existed = activeStreams.has(sessionId);
  if (existed) {
    const state = activeStreams.get(sessionId);
    console.log(`[HappyStore] Stream cleared: ${state?.messageId} in session ${sessionId} (${state?.accumulatedLength} chars)`);
  }
  activeStreams.delete(sessionId);
  return existed;
}

/**
 * DEDUP-019: Check for and log stale streams.
 * A stale stream is one that hasn't received chunks for STREAM_TIMEOUT_MS.
 * This is for monitoring purposes - does not auto-cleanup (yet).
 */
function checkStaleStreams(): void {
  const now = Date.now();
  // Convert to array for ES5 compatibility
  const entries = Array.from(activeStreams.entries());
  for (let i = 0; i < entries.length; i++) {
    const [sessionId, state] = entries[i];
    const timeSinceLastChunk = now - state.lastChunkAt;
    if (timeSinceLastChunk > STREAM_TIMEOUT_MS) {
      console.warn(`[HappyStore] Stale stream detected: ${state.messageId} in session ${sessionId}`, {
        startedAt: new Date(state.startedAt).toISOString(),
        lastChunkAt: new Date(state.lastChunkAt).toISOString(),
        timeSinceLastChunk: `${Math.round(timeSinceLastChunk / 1000)}s`,
        accumulatedLength: state.accumulatedLength,
      });
      // Note: We log but don't auto-cleanup stale streams
      // The stream might still complete, or the session might be terminated
      // Future enhancement: emit synthetic completion after extended timeout
    }
  }
}

// Start the stale stream check interval
// This runs every 10 seconds to detect streams that stopped receiving chunks
let staleCheckIntervalId: ReturnType<typeof setInterval> | null = null;

function startStaleStreamMonitor(): void {
  if (staleCheckIntervalId === null) {
    staleCheckIntervalId = setInterval(checkStaleStreams, STALE_CHECK_INTERVAL_MS);
    console.log('[HappyStore] Stale stream monitor started');
  }
}

function stopStaleStreamMonitor(): void {
  if (staleCheckIntervalId !== null) {
    clearInterval(staleCheckIntervalId);
    staleCheckIntervalId = null;
    console.log('[HappyStore] Stale stream monitor stopped');
  }
}

// Auto-start the monitor when module loads
startStaleStreamMonitor();

/**
 * DEDUP-021: Get streaming debug info for developer tools.
 * Returns a snapshot of all active streaming states.
 *
 * @returns Record of sessionId to StreamingState
 */
export function getStreamingDebugInfo(): Record<string, StreamingState & { ageMs: number; staleness: string }> {
  const now = Date.now();
  const result: Record<string, StreamingState & { ageMs: number; staleness: string }> = {};

  // Convert to array for ES5 compatibility
  const entries = Array.from(activeStreams.entries());
  for (let i = 0; i < entries.length; i++) {
    const [sessionId, state] = entries[i];
    const ageMs = now - state.startedAt;
    const timeSinceLastChunk = now - state.lastChunkAt;
    let staleness: string;

    if (timeSinceLastChunk < 5000) {
      staleness = 'active';
    } else if (timeSinceLastChunk < STREAM_TIMEOUT_MS) {
      staleness = 'idle';
    } else {
      staleness = 'stale';
    }

    result[sessionId] = {
      ...state,
      ageMs,
      staleness,
    };
  }

  return result;
}

/**
 * Get count of active streams for monitoring
 */
export function getActiveStreamCount(): number {
  return activeStreams.size;
}

/**
 * Time-to-live for message ID tracking.
 * 30 seconds is sufficient to catch race conditions while preventing memory bloat.
 */
const MESSAGE_ID_TTL_MS = 30000; // 30 seconds

/**
 * Client-side duplicate detection statistics.
 * Logs every 10 duplicates for monitoring and debugging.
 */
const clientDuplicateStats = {
  totalReceived: 0,
  duplicatesDetected: 0,
  duplicatesPrevented: 0,
};

/**
 * Track a message ID for fast duplicate detection.
 *
 * This function provides the FAST PATH for duplicate detection:
 * - O(1) lookup via Set.has()
 * - Auto-removes entries after TTL to prevent memory bloat
 * - Tracks statistics for monitoring
 *
 * @param messageId - The message ID to track
 * @returns true if the message is NEW (not a duplicate), false if already tracked (IS a duplicate)
 *
 * @example
 * ```typescript
 * if (!trackMessageId(message.message_id)) {
 *   // Duplicate detected - skip processing
 *   return state;
 * }
 * // New message - proceed with adding
 * ```
 */
function trackMessageId(messageId: string): boolean {
  clientDuplicateStats.totalReceived++;

  if (recentMessageIds.has(messageId)) {
    clientDuplicateStats.duplicatesDetected++;
    clientDuplicateStats.duplicatesPrevented++;

    // Log every 10 duplicates for monitoring
    if (clientDuplicateStats.duplicatesDetected % 10 === 0) {
      console.log('[HappyStore] Client duplicate stats:', {
        ...clientDuplicateStats,
        trackingSetSize: recentMessageIds.size,
      });
    }

    return false; // Already tracked - is duplicate
  }

  recentMessageIds.add(messageId);

  // Auto-remove after TTL to prevent memory bloat
  setTimeout(() => {
    recentMessageIds.delete(messageId);
  }, MESSAGE_ID_TTL_MS);

  return true; // New message
}

/**
 * Check if a message ID is already being tracked.
 * Used by setMessages for bulk deduplication without adding to tracking.
 *
 * @param messageId - The message ID to check
 * @returns true if the message ID is being tracked, false otherwise
 */
function isMessageIdTracked(messageId: string): boolean {
  return recentMessageIds.has(messageId);
}

/**
 * Server-synced session data structure (from relay server)
 */
export interface ServerSyncedSession {
  id: string;
  daemonId: string;
  title: string | null;
  status: 'active' | 'idle' | 'paused' | 'terminated';
  createdAt: string;
  lastActivityAt: string;
  messageCount: number;
  cwd: string | null;
  projectName: string | null;
}

/**
 * Current tool event info for status bar display
 */
export interface CurrentToolEvent {
  /** Tool type being executed */
  toolType: string;
  /** Tool description/file path/command */
  description?: string;
  /** When the tool started */
  startTime: number;
  /** Event type from daemon */
  eventType: SessionOutputEventType | null;
  /** Session ID this tool is running in */
  sessionId: string;
}

/**
 * Context usage information for showing remaining context
 */
export interface ContextInfo {
  /** Tokens used so far */
  tokensUsed: number;
  /** Maximum tokens available */
  tokensMax: number;
  /** Percentage remaining (0-100) */
  percentageRemaining: number;
  /** Last updated timestamp */
  lastUpdated: number;
  /** Percentage used (0-100), derived from percentageRemaining when absent */
  usedPercent?: number;
  /** Input token count alias emitted by some daemon builds */
  inputTokens?: number;
  /** Output token count alias emitted by some daemon builds */
  outputTokens?: number;
}

/**
 * Session health status for tracking crashed/disconnected sessions
 */
export type SessionHealthStatus = 'healthy' | 'crashed' | 'disconnected' | 'error';

/**
 * Session health information per session
 */
export interface SessionHealth {
  /** Current health status */
  status: SessionHealthStatus;
  /** Error message if session is unhealthy */
  errorMessage?: string;
  /** When the error occurred */
  errorTimestamp?: number;
}

/**
 * Happy Store State Interface
 */
export interface HappyState {
  // Connection
  connectionState: ConnectionState;
  serverUrl: string;

  // Authentication
  auth: AuthState;

  // Sessions
  sessions: HappySession[];
  activeSessionId: string | null;

  // Messages (keyed by session_id)
  messagesBySession: Record<string, HappyMessage[]>;

  // Streaming state
  streamingMessageBySession: Record<string, string | null>;

  // Agent activity
  agents: AgentActivity[];
  agentStats: AgentStats;

  // Command Center
  commandStatus: CommandStatus;
  currentExecution: CommandExecution | null;
  terminalOutput: string[];

  // UI state
  isThinking: boolean;
  backgroundTaskCount: number;

  // Context usage
  contextInfo: ContextInfo | null;

  // Current tool event for status bar display
  currentToolEvent: CurrentToolEvent | null;

  // Session health tracking (keyed by session_id)
  sessionHealthBySession: Record<string, SessionHealth>;

  // Server-side session sync
  syncStatus: 'synced' | 'syncing' | 'offline';
  lastSyncAt: number | null;

  // Daemon reconnection tracking
  daemonReconnection: DaemonReconnection | null;

  // Relay health (heartbeat) tracking
  relayHealth: RelayHealthState;

  // Multi-daemon support
  daemons: RegisteredDaemon[];
  selectedDaemonId: string | null;
  daemonStats: DaemonRegistryStats;

  // Tool call aggregation per session (for ToolCallFeed real-time display)
  toolCallAggregations: Record<string, ToolCallAggregationState>;

  // Active agents per session (for LiveAgentCard real-time display)
  activeAgents: Record<string, ActiveAgentState>;

  // Daemon logs (for visibility into parent control operations)
  daemonLogs: DaemonLogEntry[];
}

/**
 * Daemon log entry for parent control operation feedback
 */
export interface DaemonLogEntry {
  /** Log level: info, warn, error, success */
  level: 'info' | 'warn' | 'error' | 'success';
  /** Log message */
  message: string;
  /** Timestamp of the log */
  timestamp: string;
  /** Optional context (operation type, session IDs, etc.) */
  context?: {
    operation?: string;
    sessionId?: string;
    sessionCount?: number;
    terminatedCount?: number;
    restartedCount?: number;
    failedCount?: number;
    [key: string]: unknown;
  };
  /** Device ID of the daemon */
  deviceId?: string;
}

/**
 * Active agent state for LiveAgentCard real-time display
 */
export interface ActiveAgentState {
  /** Unique agent ID */
  id: string;
  /** Session this agent belongs to */
  sessionId: string;
  /** Agent type (e.g., "Task", "Skill") */
  type: string;
  /** Current execution status */
  status: 'running' | 'done' | 'error';
  /** Start time in milliseconds */
  startTime: number;
  /** End time in milliseconds (when completed) */
  endTime?: number;
  /** Number of tools used */
  toolCount: number;
  /** Token count used */
  tokenCount: number;
  /** Model name (e.g., "opus-4", "sonnet-4") */
  model?: string;
  /** Task description */
  description?: string;
  /** Parent agent ID (for nested agents) */
  parentAgentId?: string;
  /** Agent name (e.g., "explore-agent", "code-reviewer") - from subagent_type */
  name?: string;
}

/**
 * Relay health state for tracking heartbeat/ping-pong status
 */
export interface RelayHealthState {
  /** Current relay connection health */
  status: 'healthy' | 'degraded' | 'reconnecting' | 'unknown';
  /** Number of consecutive missed pongs */
  missedPongs: number;
  /** Timestamp of last successful pong */
  lastPongTime: number | null;
  /** When the health state was last updated */
  lastUpdated: number | null;
}

/**
 * Daemon reconnection information for UI notification
 */
export interface DaemonReconnection {
  daemonId: string;
  reconnectedAt: string;
  reconnectAttempt: number;
  disconnectedAt?: string;
  /** When the reconnection notification was received */
  receivedAt: number;
}

/**
 * Registered daemon information (from daemon registry)
 */
export interface RegisteredDaemon {
  id: string;
  deviceId: string;
  name: string;
  status: 'connected' | 'disconnected' | 'reconnecting';
  sessionCount: number;
  connectedAt: number;
  lastActivity: number;
  metadata?: {
    hostname?: string;
    platform?: string;
    version?: string;
  };
}

/**
 * Daemon registry statistics
 */
export interface DaemonRegistryStats {
  totalDaemons: number;
  connectedDaemons: number;
  totalSessions: number;
}

/**
 * Happy Store Actions Interface
 */
export interface HappyActions {
  // Connection
  setConnectionState: (state: ConnectionState) => void;
  setServerUrl: (url: string) => void;

  // Authentication
  setAuth: (auth: Partial<AuthState>) => void;
  logout: () => void;

  // Sessions
  setSessions: (sessions: HappySession[]) => void;
  addSession: (session: HappySession) => void;
  updateSession: (sessionId: string, updates: Partial<HappySession>) => void;
  removeSession: (sessionId: string) => void;
  setActiveSession: (sessionId: string | null) => void;

  // Session title management
  /**
   * Update session title with API call and real-time sync.
   * Uses optimistic update - reverts on API failure.
   * @param sessionId - Session ID to update
   * @param title - New title (1-100 chars, trimmed)
   */
  setSessionTitle: (sessionId: string, title: string) => Promise<void>;
  /**
   * Toggle session title lock status with API call.
   * When locked, AI auto-rename is prevented.
   * @param sessionId - Session ID to update
   * @param locked - Whether to lock the title
   */
  setSessionTitleLocked: (sessionId: string, locked: boolean) => Promise<void>;
  /**
   * Handle session:title:updated event from Socket.IO for real-time sync.
   * Called when another client updates the title.
   */
  handleSessionTitleUpdated: (payload: { session_id: string; title: string; updated_by: string; timestamp: string }) => void;
  /**
   * Handle session:lock:updated event from Socket.IO for real-time sync.
   * Called when another client toggles the title lock.
   */
  handleSessionLockUpdated: (payload: { session_id: string; title_locked: boolean; updated_by: string; timestamp: string }) => void;

  // Messages
  setMessages: (sessionId: string, messages: HappyMessage[]) => void;
  addMessage: (sessionId: string, message: HappyMessage) => void;
  updateMessage: (sessionId: string, messageId: string, updates: Partial<HappyMessage>) => void;
  appendStreamingContent: (sessionId: string, messageId: string, content: string) => void;
  finalizeStreamingMessage: (sessionId: string, messageId: string) => void;
  clearMessages: (sessionId: string) => void;

  // Agent activity
  setAgents: (agents: AgentActivity[]) => void;
  updateAgent: (agentId: string, updates: Partial<AgentActivity>) => void;
  setAgentStats: (stats: AgentStats) => void;

  // Command Center
  setCommandStatus: (status: CommandStatus) => void;
  runCommand: (command: CommandType) => void;
  stopCommand: () => void;
  restartCLI: () => void;
  appendTerminalOutput: (line: string) => void;
  clearTerminalOutput: () => void;

  // UI state
  setIsThinking: (isThinking: boolean) => void;
  setBackgroundTaskCount: (count: number) => void;
  /**
   * Reset all thinking-related state when session goes idle.
   * Fixes BUG-006: Status indicator stuck on thinking after response completes.
   */
  resetThinkingState: () => void;

  // Context usage
  setContextInfo: (info: ContextInfo | null) => void;
  updateContextFromContent: (content: string) => void;

  // Current tool event
  setCurrentToolEvent: (event: CurrentToolEvent | null) => void;
  handleToolOutputEvent: (sessionId: string, eventType: string, metadata?: { tool_name?: string; [key: string]: unknown }) => void;

  // Session health
  setSessionHealth: (sessionId: string, health: SessionHealth) => void;
  markSessionCrashed: (sessionId: string, errorMessage: string) => void;
  markSessionHealthy: (sessionId: string) => void;
  clearSessionHealth: (sessionId: string) => void;

  // Bulk session cleanup (for daemon disconnect/stop)
  clearAllSessions: () => void;

  // Hook events
  handleHookEvent: (event: HookEvent) => void;

  // Persistence
  loadFromStorage: () => void;
  saveToStorage: () => void;

  // Server sync
  setSyncStatus: (status: 'synced' | 'syncing' | 'offline') => void;
  handleServerSync: (sessions: ServerSyncedSession[], daemonId: string) => void;
  handleSessionUpdated: (session: ServerSyncedSession, daemonId: string) => void;
  handleSessionRemoved: (sessionId: string, daemonId: string) => void;
  requestSync: () => void;

  // Daemon reconnection
  setDaemonReconnection: (reconnection: Omit<DaemonReconnection, 'receivedAt'> | null) => void;
  clearDaemonReconnection: () => void;

  // Relay health (heartbeat)
  setRelayHealthy: () => void;
  setRelayUnhealthy: (missedPongs: number) => void;
  setRelayReconnecting: () => void;

  // Multi-daemon support
  setDaemons: (daemons: RegisteredDaemon[]) => void;
  updateDaemon: (daemonId: string, updates: Partial<RegisteredDaemon>) => void;
  removeDaemon: (daemonId: string) => void;
  setSelectedDaemonId: (daemonId: string | null) => void;
  setDaemonStats: (stats: DaemonRegistryStats) => void;
  handleDaemonListUpdate: (data: { daemons: RegisteredDaemon[]; stats: DaemonRegistryStats; timestamp: string }) => void;

  // Tool call aggregation (for ToolCallFeed real-time display)
  /**
   * Handle tool:start event from Socket.IO adapter.
   * Creates a new running entry in the tool call aggregation.
   */
  handleToolStart: (event: {
    sessionId: string;
    toolId: string;
    toolName: string;
    startTime: number;
    args?: Record<string, unknown>;
    /** Parent message ID for lineage binding (TSRP) */
    parentMessageId?: string;
    /** Run ID for grouping (TSRP) */
    runId?: string;
  }) => void;
  /**
   * Handle tool:end event from Socket.IO adapter.
   * Updates the entry to completed/failed status.
   */
  handleToolEnd: (event: {
    sessionId: string;
    toolId: string;
    toolName: string;
    endTime: number;
    durationMs: number;
    success: boolean;
    error?: string;
    result?: string;
  }) => void;
  /**
   * SRP/TSRP: Complete all running tools for a session.
   * Used when session is stopped or all runs complete (D1 heuristic).
   */
  handleAllToolsComplete: (sessionId: string, reason?: 'canceled' | 'completed') => void;
  /**
   * Set agent info for a session's tool call aggregation.
   */
  setToolCallAgentInfo: (sessionId: string, agentInfo: AgentInfo) => void;
  /**
   * Mark a session's tool call aggregation as completed (for auto-collapse).
   */
  markToolCallsCompleted: (sessionId: string) => void;
  /**
   * Clear tool call aggregation for a session.
   */
  clearToolCallAggregation: (sessionId: string) => void;
  /**
   * Get tool call aggregation state for a session.
   */
  getToolCallState: (sessionId: string) => ToolCallAggregationState | undefined;

  // Agent lifecycle events (for LiveAgentCard real-time display)
  /**
   * Handle agent:spawn event from Socket.IO adapter.
   * Creates a new running agent in the active agents state.
   */
  handleAgentSpawn: (event: {
    sessionId: string;
    agentId: string;
    spawnTime: number;
    parentAgentId?: string;
    taskDescription?: string;
    agentName?: string;
    estimatedTokens?: number;
  }) => void;
  /**
   * Handle agent:update event from Socket.IO adapter.
   * Updates the agent's status, tokens, and progress.
   */
  handleAgentUpdate: (event: {
    sessionId: string;
    agentId: string;
    status?: string;
    tokensUsed?: number;
    toolCount?: number;
    progressPercentage?: number;
    currentAction?: string;
  }) => void;
  /**
   * Handle agent:complete event from Socket.IO adapter.
   * Marks the agent as done/error and records final stats.
   */
  handleAgentComplete: (event: {
    sessionId: string;
    agentId: string;
    endTime: number;
    durationMs?: number;
    success: boolean;
    tokensUsed: number;
    toolCount?: number;
    error?: string;
  }) => void;
  /**
   * Get active agent for a session.
   */
  getActiveAgent: (sessionId: string) => ActiveAgentState | undefined;
  /**
   * Clear active agent for a session.
   */
  clearActiveAgent: (sessionId: string) => void;

  // Daemon logs (for parent control operation feedback)
  /**
   * Add a daemon log entry.
   * Logs are kept in a rolling buffer (max 50 entries).
   */
  addDaemonLog: (log: DaemonLogEntry) => void;
  /**
   * Clear all daemon logs.
   */
  clearDaemonLogs: () => void;

  // Reset
  reset: () => void;
}

/**
 * Initial state
 */
const initialState: HappyState = {
  connectionState: 'disconnected',
  serverUrl: 'https://tools.eroland.me',

  auth: {
    authenticated: false,
    device_id: undefined,
    pairing_code: undefined,
    encryption_keypair: undefined,
  },

  sessions: [],
  activeSessionId: null,

  messagesBySession: {},

  streamingMessageBySession: {},

  agents: [],
  agentStats: {
    total_tools_executed: 0,
    tools_completed: 0,
    tools_running: 0,
    tools_failed: 0,
    total_duration_ms: 0,
  },

  commandStatus: 'ready',
  currentExecution: null,
  terminalOutput: [],

  isThinking: false,
  backgroundTaskCount: 0,

  contextInfo: null,

  currentToolEvent: null,

  sessionHealthBySession: {},

  // Server sync state
  syncStatus: 'offline',
  lastSyncAt: null,

  // Daemon reconnection tracking
  daemonReconnection: null,

  // Relay health tracking
  relayHealth: {
    status: 'unknown',
    missedPongs: 0,
    lastPongTime: null,
    lastUpdated: null,
  },

  // Multi-daemon support
  daemons: [],
  selectedDaemonId: null,
  daemonStats: {
    totalDaemons: 0,
    connectedDaemons: 0,
    totalSessions: 0,
  },

  // Tool call aggregation per session
  toolCallAggregations: {},

  // Active agents per session
  activeAgents: {},

  // Daemon logs for parent control operations
  daemonLogs: [],
};

/**
 * Happy Zustand Store
 *
 * Manages all Happy Engineering UI state including:
 * - WebSocket connection state
 * - Authentication and encryption keypair
 * - Sessions and messages
 * - Agent activity and tool calls
 * - Command Center state
 *
 * @example
 * ```tsx
 * const { sessions, activeSessionId, setActiveSession } = useHappyStore();
 * ```
 */
export const useHappyStore = create<HappyState & HappyActions>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      ...initialState,

      // Connection actions
      setConnectionState: (connectionState) => set({ connectionState }),
      setServerUrl: (serverUrl) => set({ serverUrl }),

      // Authentication actions
      setAuth: (auth) =>
        set((state) => ({
          auth: { ...state.auth, ...auth },
        })),

      logout: () =>
        set({
          auth: initialState.auth,
          sessions: [],
          activeSessionId: null,
          messagesBySession: {},
          connectionState: 'disconnected',
        }),

      // Session actions
      setSessions: (sessions) =>
        set((state) => {
          // IMPORTANT: REPLACE sessions, not merge
          // This prevents "stale session" bug where sessions from a previous
          // daemon run persist in UI state and cause "Session not found" errors.
          //
          // Sessions are now server-managed (no localStorage), so we should
          // trust the server's session list as the source of truth.
          // New sessions from session:created use addSession() instead.

          // Create a map of existing sessions for quick lookup
          const existingSessionsMap = new Map(
            state.sessions.map((s) => [s.session_id, s])
          );

          // Replace with incoming sessions, but preserve local UI state (like title, messages count)
          const replacedSessions: HappySession[] = [];

          for (const session of sessions) {
            const existing = existingSessionsMap.get(session.session_id);
            if (existing) {
              // Merge: preserve local data (like title, messages count) but update from server
              replacedSessions.push({ ...existing, ...session });
            } else {
              replacedSessions.push(session);
            }
          }

          // NOTE: We no longer keep sessions that aren't in the incoming list!
          // This prevents stale sessions from persisting after daemon restart.

          console.log('[HappyStore] setSessions: replaced', {
            incoming: sessions.length,
            existing: state.sessions.length,
            result: replacedSessions.length,
          });

          return { sessions: replacedSessions };
        }),

      addSession: (session) =>
        set((state) => {
          // Check for duplicate session by session_id
          const existingIndex = state.sessions.findIndex(
            (s) => s.session_id === session.session_id
          );

          if (existingIndex !== -1) {
            // Session already exists - update it instead of adding duplicate
            console.log('[HappyStore] Session already exists, updating:', session.session_id);
            return {
              sessions: state.sessions.map((s, i) =>
                i === existingIndex ? { ...s, ...session } : s
              ),
            };
          }

          // New session - add to beginning
          console.log('[HappyStore] Adding new session:', session.session_id);
          return {
            sessions: [session, ...state.sessions],
          };
        }),

      updateSession: (sessionId, updates) =>
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.session_id === sessionId ? { ...s, ...updates } : s
          ),
        })),

      removeSession: (sessionId) =>
        set((state) => ({
          sessions: state.sessions.filter((s) => s.session_id !== sessionId),
          activeSessionId:
            state.activeSessionId === sessionId ? null : state.activeSessionId,
          messagesBySession: Object.fromEntries(
            Object.entries(state.messagesBySession).filter(
              ([id]) => id !== sessionId
            )
          ),
        })),

      setActiveSession: (activeSessionId) => set({ activeSessionId }),

      // Session title management actions
      setSessionTitle: async (sessionId, title) => {
        const trimmedTitle = title.trim();
        if (!trimmedTitle || trimmedTitle.length > 100) {
          throw new Error('Title must be 1-100 characters');
        }

        // Get current title for rollback
        const currentSession = get().sessions.find((s) => s.session_id === sessionId);
        const previousTitle = currentSession?.title;

        // Optimistic update
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.session_id === sessionId ? { ...s, title: trimmedTitle } : s
          ),
        }));

        try {
          // API call will be made via socket adapter or REST API
          // For now, we emit via socket if available
          const { selfHostedService } = await import('../services/selfHostedService');
          await selfHostedService.updateSessionTitle(sessionId, trimmedTitle);
          console.log('[HappyStore] Session title updated:', { sessionId, title: trimmedTitle });
        } catch (error) {
          // Rollback on error
          set((state) => ({
            sessions: state.sessions.map((s) =>
              s.session_id === sessionId ? { ...s, title: previousTitle } : s
            ),
          }));
          console.error('[HappyStore] Failed to update session title:', error);
          throw error;
        }
      },

      setSessionTitleLocked: async (sessionId, locked) => {
        // Get current lock state for rollback
        const currentSession = get().sessions.find((s) => s.session_id === sessionId);
        const previousLocked = currentSession?.title_locked;

        // Optimistic update
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.session_id === sessionId ? { ...s, title_locked: locked } : s
          ),
        }));

        try {
          // API call will be made via socket adapter or REST API
          const { selfHostedService } = await import('../services/selfHostedService');
          await selfHostedService.updateSessionTitleLock(sessionId, locked);
          console.log('[HappyStore] Session title lock updated:', { sessionId, locked });
        } catch (error) {
          // Rollback on error
          set((state) => ({
            sessions: state.sessions.map((s) =>
              s.session_id === sessionId ? { ...s, title_locked: previousLocked } : s
            ),
          }));
          console.error('[HappyStore] Failed to update session title lock:', error);
          throw error;
        }
      },

      handleSessionTitleUpdated: (payload) => {
        console.log('[HappyStore] Real-time title update received:', payload);
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.session_id === payload.session_id
              ? { ...s, title: payload.title, last_activity_at: payload.timestamp }
              : s
          ),
        }));
      },

      handleSessionLockUpdated: (payload) => {
        console.log('[HappyStore] Real-time lock update received:', payload);
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.session_id === payload.session_id
              ? { ...s, title_locked: payload.title_locked, last_activity_at: payload.timestamp }
              : s
          ),
        }));
      },

      // Message actions
      setMessages: (sessionId, messages) =>
        set((state) => {
          const existingMessages = state.messagesBySession[sessionId] || [];

          // If no existing messages, just set the new ones
          if (existingMessages.length === 0) {
            return {
              messagesBySession: {
                ...state.messagesBySession,
                [sessionId]: messages,
              },
            };
          }

          // BUG-007 FIX: Helper to check if messages are duplicates by content
          // (for user messages added optimistically with local IDs)
          const isDuplicateByContent = (msg: HappyMessage, existing: HappyMessage): boolean => {
            if (msg.role !== existing.role) return false;
            if (msg.content !== existing.content) return false;
            // Check if timestamps are within 10 seconds of each other
            const msgTime = new Date(msg.created_at).getTime();
            const existingTime = new Date(existing.created_at).getTime();
            return Math.abs(msgTime - existingTime) < 10000; // 10 seconds
          };

          // Merge: create a map of existing messages by ID
          const existingMap = new Map(
            existingMessages.map((m) => [m.message_id, m])
          );

          // Merge incoming messages with existing (incoming takes precedence)
          const mergedMessages: HappyMessage[] = [];
          const seenIds = new Set<string>();

          // First add all existing messages (preserve order)
          for (const msg of existingMessages) {
            seenIds.add(msg.message_id);
            const incoming = messages.find((m) => m.message_id === msg.message_id);
            if (incoming) {
              // Update with incoming data
              mergedMessages.push({ ...msg, ...incoming });
            } else {
              // Keep existing message
              mergedMessages.push(msg);
            }
          }

          // Then add any new messages from incoming that weren't in existing
          for (const msg of messages) {
            if (!seenIds.has(msg.message_id)) {
              // ============================================
              // FAST PATH: Check tracking Set first (O(1) lookup)
              // ============================================
              if (isMessageIdTracked(msg.message_id)) {
                console.log('[HappyStore] setMessages FAST DUPLICATE by tracking Set:', msg.message_id);
                clientDuplicateStats.duplicatesDetected++;
                clientDuplicateStats.duplicatesPrevented++;
                continue;
              }

              // ============================================
              // SLOW PATH: Content-based duplicate check
              // ============================================
              // BUG-007 FIX: Also check for content-based duplicates
              // This catches user messages with different IDs but same content
              const hasDuplicateByContent = mergedMessages.some((existing) =>
                isDuplicateByContent(msg, existing)
              );
              if (hasDuplicateByContent) {
                console.log('[HappyStore] setMessages duplicate by content:', msg.message_id);
                clientDuplicateStats.duplicatesDetected++;
                clientDuplicateStats.duplicatesPrevented++;
                continue;
              }

              // Track this message ID for future fast-path checks
              trackMessageId(msg.message_id);
              mergedMessages.push(msg);
            }
          }

          // Sort by created_at to maintain chronological order
          mergedMessages.sort((a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );

          console.log('[HappyStore] setMessages: merged', {
            sessionId,
            existing: existingMessages.length,
            incoming: messages.length,
            merged: mergedMessages.length,
          });

          return {
            messagesBySession: {
              ...state.messagesBySession,
              [sessionId]: mergedMessages,
            },
          };
        }),

      addMessage: (sessionId, message) =>
        set((state) => {
          // ============================================
          // FAST PATH: Check tracking Set first (O(1) lookup)
          // This catches duplicates before any array iteration
          // ============================================
          if (!trackMessageId(message.message_id)) {
            console.log('[HappyStore] FAST DUPLICATE DETECTED:', message.message_id);
            return state; // No change
          }

          const existingMessages = state.messagesBySession[sessionId] || [];

          // ============================================
          // SLOW PATH: Check array for edge cases
          // ============================================
          // CHECK FOR DUPLICATE BY ID - prevent race conditions from socket events
          const alreadyExistsById = existingMessages.some(
            (m) => m.message_id === message.message_id
          );
          if (alreadyExistsById) {
            console.log(
              '[HappyStore] Skipping duplicate message (array check):',
              message.message_id
            );
            return state; // No change
          }

          // BUG-007 FIX: Also check for content-based duplicates
          // This catches messages with different IDs but same content (e.g., optimistic vs server)
          const msgTime = new Date(message.created_at).getTime();
          const alreadyExistsByContent = existingMessages.some((m) => {
            if (m.role !== message.role) return false;
            if (m.content !== message.content) return false;
            const existingTime = new Date(m.created_at).getTime();
            return Math.abs(msgTime - existingTime) < 10000; // 10 seconds
          });
          if (alreadyExistsByContent) {
            console.log(
              '[HappyStore] Skipping duplicate by content:',
              message.message_id
            );
            // Update stats for content-based duplicates too
            clientDuplicateStats.duplicatesDetected++;
            clientDuplicateStats.duplicatesPrevented++;
            return state; // No change
          }

          const newMessages = [...existingMessages, message];

          // Sort by created_at to maintain chronological order (oldest first)
          newMessages.sort((a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
          const userMessages = newMessages.filter((m) => m.role === 'user');
          const userMessageCount = userMessages.length;

          const isFirstUserMessage =
            message.role === 'user' &&
            !existingMessages.some((m) => m.role === 'user');

          // Generate smart title on first user message
          let updatedSessions = state.sessions;
          if (isFirstUserMessage) {
            const session = state.sessions.find((s) => s.session_id === sessionId);
            if (session && !session.title) {
              // Set immediate fallback title
              const fallbackTitle = generateSessionTitle(message.content);
              updatedSessions = state.sessions.map((s) =>
                s.session_id === sessionId ? { ...s, title: fallbackTitle } : s
              );

              // Trigger async AI title generation (fire and forget)
              generateAiSessionTitle(message.content, sessionId)
                .then(({ title: aiTitle }) => {
                  // Update title with AI-generated version
                  get().updateSession(sessionId, { title: aiTitle });
                })
                .catch((err) => {
                  console.warn('[happyStore] AI title generation failed:', err);
                });
            }
          }
          // Auto-update title every 5 user messages if topic has changed
          else if (
            message.role === 'user' &&
            userMessageCount > 1 &&
            userMessageCount % 5 === 0
          ) {
            const session = state.sessions.find((s) => s.session_id === sessionId);
            if (session?.title && message.content) {
              if (hasTopicChanged(session.title, message.content)) {
                // Set immediate fallback title
                const fallbackTitle = generateSessionTitle(message.content);
                updatedSessions = state.sessions.map((s) =>
                  s.session_id === sessionId ? { ...s, title: fallbackTitle } : s
                );

                // Trigger async AI title generation (fire and forget)
                generateAiSessionTitle(message.content, sessionId)
                  .then(({ title: aiTitle }) => {
                    get().updateSession(sessionId, { title: aiTitle });
                  })
                  .catch((err) => {
                    console.warn('[happyStore] AI title generation failed:', err);
                  });
              }
            }
          }

          return {
            sessions: updatedSessions,
            messagesBySession: {
              ...state.messagesBySession,
              [sessionId]: newMessages,
            },
          };
        }),

      updateMessage: (sessionId, messageId, updates) =>
        set((state) => ({
          messagesBySession: {
            ...state.messagesBySession,
            [sessionId]: (state.messagesBySession[sessionId] || []).map((m) =>
              m.message_id === messageId ? { ...m, ...updates } : m
            ),
          },
        })),

      appendStreamingContent: (sessionId, messageId, content) =>
        set((state) => ({
          messagesBySession: {
            ...state.messagesBySession,
            [sessionId]: (state.messagesBySession[sessionId] || []).map((m) =>
              m.message_id === messageId ? { ...m, content: m.content + content } : m
            ),
          },
        })),

      finalizeStreamingMessage: (sessionId, messageId) =>
        set((state) => {
          // DEDUP-020: Clear active stream state if this is the active stream
          // This prevents double finalization and keeps tracking in sync
          const activeStream = getActiveStream(sessionId);
          if (activeStream?.messageId === messageId) {
            clearActiveStream(sessionId);
          } else if (activeStream) {
            // Different message ID - log warning but still finalize
            console.warn(`[HappyStore] finalizeStreamingMessage: messageId mismatch`, {
              finalizingId: messageId,
              activeStreamId: activeStream.messageId,
              sessionId,
            });
          }

          return {
            messagesBySession: {
              ...state.messagesBySession,
              [sessionId]: (state.messagesBySession[sessionId] || []).map((m) =>
                m.message_id === messageId ? { ...m, _isStreaming: undefined } : m
              ),
            },
          };
        }),

      clearMessages: (sessionId) =>
        set((state) => {
          // Create new messagesBySession without the session's messages
          const { [sessionId]: _, ...rest } = state.messagesBySession;
          console.log('[HappyStore] Cleared messages for session:', sessionId);
          return {
            messagesBySession: {
              ...rest,
              [sessionId]: [], // Set to empty array to preserve session
            },
          };
        }),

      // Agent actions
      setAgents: (agents) => set({ agents }),

      updateAgent: (agentId, updates) =>
        set((state) => ({
          agents: state.agents.map((a) =>
            a.agent_id === agentId ? { ...a, ...updates } : a
          ),
        })),

      setAgentStats: (agentStats) => set({ agentStats }),

      // Command Center actions
      setCommandStatus: (commandStatus) => set({ commandStatus }),

      runCommand: (command) => {
        const timestamp = new Date().toISOString();
        set({
          commandStatus: 'running',
          currentExecution: {
            command,
            status: 'running',
            started_at: timestamp,
            output: [],
          },
        });
        // Add command to terminal output
        get().appendTerminalOutput(`$ Running /${command}...`);
      },

      stopCommand: () => {
        set((state) => ({
          commandStatus: 'stopped',
          currentExecution: state.currentExecution
            ? { ...state.currentExecution, status: 'stopped' }
            : null,
        }));
        get().appendTerminalOutput('✗ Command stopped by user');
      },

      restartCLI: () => {
        set({
          commandStatus: 'ready',
          currentExecution: null,
          terminalOutput: ['$ CLI restarted'],
        });
      },

      appendTerminalOutput: (line) =>
        set((state) => ({
          terminalOutput: [...state.terminalOutput.slice(-100), line], // Keep last 100 lines
        })),

      clearTerminalOutput: () => set({ terminalOutput: [] }),

      // UI state actions
      setIsThinking: (isThinking) => set({ isThinking }),
      setBackgroundTaskCount: (backgroundTaskCount) => set({ backgroundTaskCount }),

      // BUG-006 FIX: Reset all thinking-related state when session goes idle
      resetThinkingState: () =>
        set((state) => ({
          isThinking: false,
          currentToolEvent: null,
          agentStats: {
            ...state.agentStats,
            tools_running: 0,
          },
        })),

      // Context usage actions
      setContextInfo: (contextInfo) => set({ contextInfo }),

      updateContextFromContent: (content) => {
        // Parse context information from streaming content
        // Matches patterns like:
        // - "Context: 45K/200K" or "Context: 45,000/200,000"
        // - "45K tokens used" "out of 200K"
        // - "[Context 23%]" or "Context remaining: 77%"
        // - "tokens: 45000/200000"

        if (!content) return;

        // Pattern 1: "Context: 45K/200K" or "45,000/200,000 tokens"
        const tokenPattern = /(?:context|tokens?)[\s:]*(\d+(?:,\d{3})*\.?\d*)\s*[kK]?\s*[/]\s*(\d+(?:,\d{3})*\.?\d*)\s*[kK]?/i;
        const tokenMatch = content.match(tokenPattern);

        if (tokenMatch) {
          const parseNum = (s: string): number => {
            const cleaned = s.replace(/,/g, '');
            const num = parseFloat(cleaned);
            // Check if the original had 'K' suffix (look ahead in match)
            return num;
          };

          let used = parseNum(tokenMatch[1]);
          let max = parseNum(tokenMatch[2]);

          // Handle K suffix (e.g., "45K" = 45000)
          const usedHasK = /\d+\s*[kK]/.test(tokenMatch[0].split('/')[0]);
          const maxHasK = /\d+\s*[kK]/.test(tokenMatch[0].split('/')[1]);
          if (usedHasK && used < 1000) used *= 1000;
          if (maxHasK && max < 1000) max *= 1000;

          const remaining = Math.max(0, Math.min(100, ((max - used) / max) * 100));

          set({
            contextInfo: {
              tokensUsed: used,
              tokensMax: max,
              percentageRemaining: remaining,
              lastUpdated: Date.now(),
            },
          });
          return;
        }

        // Pattern 2: Percentage patterns like "[Context 23%]" or "77% remaining"
        const percentPattern = /(?:context|remaining)[\s:]*(\d+(?:\.\d+)?)\s*%|(\d+(?:\.\d+)?)\s*%\s*(?:context|remaining)/i;
        const percentMatch = content.match(percentPattern);

        if (percentMatch) {
          const percent = parseFloat(percentMatch[1] || percentMatch[2]);
          // Determine if this is "used" or "remaining" based on context
          const isRemaining = /remaining/i.test(content);
          const remaining = isRemaining ? percent : (100 - percent);

          // Estimate tokens based on typical 200K context window
          const estimatedMax = 200000;
          const estimatedUsed = Math.round(estimatedMax * (1 - remaining / 100));

          set({
            contextInfo: {
              tokensUsed: estimatedUsed,
              tokensMax: estimatedMax,
              percentageRemaining: remaining,
              lastUpdated: Date.now(),
            },
          });
          return;
        }

        // Pattern 3: Simple token count like "Used 45000 tokens" (estimate max as 200K)
        const simpleTokenPattern = /(?:used|consumed)\s*(\d+(?:,\d{3})*)\s*tokens?/i;
        const simpleMatch = content.match(simpleTokenPattern);

        if (simpleMatch) {
          const used = parseInt(simpleMatch[1].replace(/,/g, ''), 10);
          const estimatedMax = 200000;
          const remaining = Math.max(0, Math.min(100, ((estimatedMax - used) / estimatedMax) * 100));

          set({
            contextInfo: {
              tokensUsed: used,
              tokensMax: estimatedMax,
              percentageRemaining: remaining,
              lastUpdated: Date.now(),
            },
          });
        }
      },

      // Current tool event actions
      setCurrentToolEvent: (currentToolEvent) => set({ currentToolEvent }),

      handleToolOutputEvent: (sessionId, eventType, metadata) => {
        // Handle tool events from daemon output
        if (eventType === 'TOOL_USE_STARTED' && metadata?.tool_name) {
          set({
            currentToolEvent: {
              toolType: metadata.tool_name,
              description: undefined,
              startTime: Date.now(),
              eventType: 'TOOL_USE_STARTED',
              sessionId,
            },
            isThinking: true,
          });
        } else if (eventType === 'TOOL_USE_COMPLETED') {
          // Clear the current tool event after completion
          const current = get().currentToolEvent;
          if (current && current.sessionId === sessionId) {
            set({ currentToolEvent: null });
          }
        } else if (eventType === 'RESPONSE_COMPLETED' || eventType === 'WAITING_FOR_INPUT') {
          // Clear tool event when response completes
          set({ currentToolEvent: null, isThinking: false });
        } else if (eventType === 'RESPONSE_STARTED') {
          set({ isThinking: true });
        }
      },

      // Session health actions
      setSessionHealth: (sessionId, health) =>
        set((state) => ({
          sessionHealthBySession: {
            ...state.sessionHealthBySession,
            [sessionId]: health,
          },
        })),

      markSessionCrashed: (sessionId, errorMessage) =>
        set((state) => ({
          sessionHealthBySession: {
            ...state.sessionHealthBySession,
            [sessionId]: {
              status: 'crashed',
              errorMessage,
              errorTimestamp: Date.now(),
            },
          },
          // Also update the session status to terminated
          sessions: state.sessions.map((s) =>
            s.session_id === sessionId ? { ...s, status: 'terminated' as const } : s
          ),
        })),

      markSessionHealthy: (sessionId) =>
        set((state) => ({
          sessionHealthBySession: {
            ...state.sessionHealthBySession,
            [sessionId]: {
              status: 'healthy',
              errorMessage: undefined,
              errorTimestamp: undefined,
            },
          },
        })),

      clearSessionHealth: (sessionId) =>
        set((state) => {
          const { [sessionId]: _, ...rest } = state.sessionHealthBySession;
          return { sessionHealthBySession: rest };
        }),

      // Bulk session cleanup - clears all sessions, messages, and health state
      // Used when daemon disconnects or is stopped to provide a clean slate
      clearAllSessions: () =>
        set({
          sessions: [],
          activeSessionId: null,
          messagesBySession: {},
          streamingMessageBySession: {},
          sessionHealthBySession: {},
          isThinking: false,
          contextInfo: null,
          agents: [],
          agentStats: {
            total_tools_executed: 0,
            tools_completed: 0,
            tools_running: 0,
            tools_failed: 0,
            total_duration_ms: 0,
          },
          backgroundTaskCount: 0,
        }),

      // Hook event handler
      handleHookEvent: (event) => {
        const state = get();

        switch (event.event_type) {
          case 'ToolStart': {
            // Update agent with new tool call
            const agentId = (event.data.agent_id as string) || 'main';
            const existingAgent = state.agents.find((a) => a.agent_id === agentId);

            if (existingAgent) {
              get().updateAgent(agentId, {
                status: 'running',
                tool_calls: [
                  ...existingAgent.tool_calls,
                  {
                    tool_call_id: event.event_id,
                    tool_type: event.data.tool_type as any,
                    status: 'running',
                    started_at: event.timestamp,
                    command: event.data.command as string,
                    file_path: event.data.file_path as string,
                    pattern: event.data.pattern as string,
                    task_description: event.data.description as string,
                  },
                ],
              });
            }

            set((s) => ({
              agentStats: {
                ...s.agentStats,
                tools_running: s.agentStats.tools_running + 1,
              },
            }));
            break;
          }

          case 'ToolComplete': {
            const agentId = (event.data.agent_id as string) || 'main';
            const existingAgent = state.agents.find((a) => a.agent_id === agentId);

            if (existingAgent) {
              get().updateAgent(agentId, {
                tool_calls: existingAgent.tool_calls.map((t) =>
                  t.tool_call_id === event.data.tool_call_id
                    ? {
                        ...t,
                        status: 'completed',
                        completed_at: event.timestamp,
                        output: event.data.output as string,
                        duration_ms: event.data.duration_ms as number,
                      }
                    : t
                ),
              });
            }

            set((s) => ({
              agentStats: {
                ...s.agentStats,
                tools_running: Math.max(0, s.agentStats.tools_running - 1),
                tools_completed: s.agentStats.tools_completed + 1,
              },
            }));
            break;
          }

          case 'ToolError': {
            const agentId = (event.data.agent_id as string) || 'main';
            const existingAgent = state.agents.find((a) => a.agent_id === agentId);

            if (existingAgent) {
              get().updateAgent(agentId, {
                tool_calls: existingAgent.tool_calls.map((t) =>
                  t.tool_call_id === event.data.tool_call_id
                    ? {
                        ...t,
                        status: 'failed',
                        completed_at: event.timestamp,
                        error: event.data.error as string,
                      }
                    : t
                ),
              });
            }

            set((s) => ({
              agentStats: {
                ...s.agentStats,
                tools_running: Math.max(0, s.agentStats.tools_running - 1),
                tools_failed: s.agentStats.tools_failed + 1,
              },
            }));
            break;
          }

          case 'SubagentStart': {
            const newAgent: AgentActivity = {
              agent_id: event.data.agent_id as string,
              agent_type: 'task',
              agent_name: (event.data.agent_name as string) || 'background',
              status: 'running',
              started_at: event.timestamp,
              current_task: event.data.description as string,
              tool_calls: [],
            };
            set((s) => ({
              agents: [...s.agents, newAgent],
              backgroundTaskCount: s.backgroundTaskCount + 1,
            }));
            break;
          }

          case 'SubagentComplete': {
            const agentId = event.data.agent_id as string;
            get().updateAgent(agentId, {
              status: 'completed',
            });
            set((s) => ({
              backgroundTaskCount: Math.max(0, s.backgroundTaskCount - 1),
            }));
            break;
          }

          default:
            console.log('Unknown hook event:', event.event_type);
        }
      },

      // Persistence actions - NOW NO-OPS (localStorage removed)
      // Sessions and messages come from server via WebSocket events
      loadFromStorage: () => {
        console.log('[HappyStore] loadFromStorage is NO-OP (localStorage removed)');
        // Sessions now come from server via WebSocket, not localStorage
        // No state modification needed - server will push sessions on connect
      },

      saveToStorage: () => {
        console.log('[HappyStore] saveToStorage is NO-OP (localStorage removed)');
        // No localStorage persistence - all data is server-managed
      },

      // Server sync actions
      setSyncStatus: (syncStatus) => set({ syncStatus }),

      handleServerSync: (serverSessions, daemonId) => {
        console.log('[HappyStore] Server sync received:', {
          count: serverSessions.length,
          daemonId,
        });

        // Convert server sessions to HappySession format
        const normalizedSessions: HappySession[] = serverSessions.map((s) => ({
          session_id: s.id,
          session_name: s.title || 'Untitled Session',
          status: s.status === 'active' ? 'active' : s.status === 'terminated' ? 'terminated' : 'idle',
          created_at: s.createdAt,
          last_activity_at: s.lastActivityAt,
          message_count: s.messageCount,
          unread_count: 0,
          title: s.title || undefined,
          cwd: s.cwd || undefined,
          projectName: s.projectName || undefined,
        }));

        // Use existing setSessions to merge (it preserves local data)
        get().setSessions(normalizedSessions);

        set({
          syncStatus: 'synced',
          lastSyncAt: Date.now(),
        });
      },

      handleSessionUpdated: (session, daemonId) => {
        console.log('[HappyStore] Session updated from server:', {
          sessionId: session.id,
          daemonId,
        });

        // Update the specific session
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.session_id === session.id
              ? {
                  ...s,
                  session_name: session.title || s.session_name,
                  status: session.status === 'active' ? 'active' : session.status === 'terminated' ? 'terminated' : 'idle',
                  last_activity_at: session.lastActivityAt,
                  message_count: Math.max(s.message_count, session.messageCount),
                  title: session.title || s.title,
                }
              : s
          ),
          syncStatus: 'synced',
          lastSyncAt: Date.now(),
        }));
      },

      handleSessionRemoved: (sessionId, daemonId) => {
        console.log('[HappyStore] Session removed from server:', {
          sessionId,
          daemonId,
        });

        // Remove the session
        get().removeSession(sessionId);

        set({
          syncStatus: 'synced',
          lastSyncAt: Date.now(),
        });
      },

      requestSync: () => {
        // This will be called by the adapter to request a fresh sync
        // The actual socket.emit is done in the adapter
        set({ syncStatus: 'syncing' });
      },

      // Daemon reconnection actions
      setDaemonReconnection: (reconnection) => {
        if (!reconnection) {
          set({ daemonReconnection: null });
          return;
        }
        const fullReconnection: DaemonReconnection = {
          ...reconnection,
          receivedAt: Date.now(),
        };
        set({ daemonReconnection: fullReconnection });
        console.log('[HappyStore] Daemon reconnection recorded:', fullReconnection);

        // Auto-clear after 10 seconds
        setTimeout(() => {
          const current = get().daemonReconnection;
          if (current && current.receivedAt === fullReconnection.receivedAt) {
            set({ daemonReconnection: null });
            console.log('[HappyStore] Daemon reconnection notification cleared');
          }
        }, 10000);
      },

      clearDaemonReconnection: () => {
        set({ daemonReconnection: null });
      },

      // Relay health actions
      setRelayHealthy: () => {
        set({
          relayHealth: {
            status: 'healthy',
            missedPongs: 0,
            lastPongTime: Date.now(),
            lastUpdated: Date.now(),
          },
        });
      },

      setRelayUnhealthy: (missedPongs: number) => {
        set({
          relayHealth: {
            status: 'degraded',
            missedPongs,
            lastPongTime: get().relayHealth.lastPongTime,
            lastUpdated: Date.now(),
          },
        });
      },

      setRelayReconnecting: () => {
        set({
          relayHealth: {
            status: 'reconnecting',
            missedPongs: get().relayHealth.missedPongs,
            lastPongTime: get().relayHealth.lastPongTime,
            lastUpdated: Date.now(),
          },
        });
      },

      // Multi-daemon support actions
      setDaemons: (daemons) => {
        set({ daemons });

        // Auto-select first daemon if none selected and daemons exist
        const state = get();
        if (!state.selectedDaemonId && daemons.length > 0) {
          set({ selectedDaemonId: daemons[0].id });
          console.log('[HappyStore] Auto-selected first daemon:', daemons[0].id);
        }
      },

      updateDaemon: (daemonId, updates) =>
        set((state) => ({
          daemons: state.daemons.map((d) =>
            d.id === daemonId ? { ...d, ...updates } : d
          ),
        })),

      removeDaemon: (daemonId) =>
        set((state) => {
          const newDaemons = state.daemons.filter((d) => d.id !== daemonId);
          // Clear selection if selected daemon was removed
          const newSelectedId = state.selectedDaemonId === daemonId
            ? (newDaemons.length > 0 ? newDaemons[0].id : null)
            : state.selectedDaemonId;

          console.log('[HappyStore] Daemon removed:', daemonId, 'New selection:', newSelectedId);

          return {
            daemons: newDaemons,
            selectedDaemonId: newSelectedId,
          };
        }),

      setSelectedDaemonId: (daemonId) => {
        console.log('[HappyStore] Selected daemon changed:', daemonId);
        set({ selectedDaemonId: daemonId });
      },

      setDaemonStats: (daemonStats) => set({ daemonStats }),

      handleDaemonListUpdate: (data) => {
        console.log('[HappyStore] Daemon list update received:', {
          count: data.daemons.length,
          stats: data.stats,
          timestamp: data.timestamp,
        });

        set({
          daemons: data.daemons,
          daemonStats: data.stats,
        });

        // Auto-select first daemon if none selected and daemons exist
        const state = get();
        if (!state.selectedDaemonId && data.daemons.length > 0) {
          set({ selectedDaemonId: data.daemons[0].id });
          console.log('[HappyStore] Auto-selected first daemon:', data.daemons[0].id);
        }
        // Clear selection if selected daemon is no longer in list
        else if (state.selectedDaemonId && !data.daemons.find((d) => d.id === state.selectedDaemonId)) {
          const newSelectedId = data.daemons.length > 0 ? data.daemons[0].id : null;
          set({ selectedDaemonId: newSelectedId });
          console.log('[HappyStore] Selected daemon no longer available, switched to:', newSelectedId);
        }
      },

      // Tool call aggregation actions (for ToolCallFeed real-time display)
      handleToolStart: (event) => {
        const { sessionId, toolId, toolName, startTime, args, parentMessageId, runId } = event;

        set((state) => {
          const existing = state.toolCallAggregations[sessionId] || {
            entries: [],
            totalCount: 0,
            runningCount: 0,
            completedCount: 0,
            failedCount: 0,
            startTime: null,
            elapsedMs: 0,
            isCompleted: false,
            agentInfo: null,
          };

          // Extract file path, command, and description from args
          const filename = args?.file_path as string | undefined;
          const command = args?.command as string | undefined;
          const description = args?.description as string | undefined;

          // Create new entry with startTime alias for consistency
          // Include TSRP lineage fields for proper parent binding
          const newEntry: ToolCallEntry = {
            id: toolId,
            sequenceNumber: existing.totalCount + 1,
            toolType: toolName,
            filename,
            command,
            description,
            status: 'running',
            startedAt: startTime,
            startTime: startTime, // alias for useLiveDuration hook
            parentMessageId, // TSRP: links tool to parent message
            runId, // TSRP: groups tool with run
          };

          // Set start time if this is the first entry
          const newStartTime = existing.startTime || startTime;

          return {
            toolCallAggregations: {
              ...state.toolCallAggregations,
              [sessionId]: {
                ...existing,
                entries: [newEntry, ...existing.entries].slice(0, 1000), // newest first, max 1000
                totalCount: existing.totalCount + 1,
                runningCount: existing.runningCount + 1,
                startTime: newStartTime,
                isCompleted: false,
              },
            },
          };
        });

        console.log('[HappyStore] Tool start:', { sessionId, toolId, toolName });
      },

      handleToolEnd: (event) => {
        const { sessionId, toolId, durationMs, success, error, result } = event;

        set((state) => {
          const existing = state.toolCallAggregations[sessionId];
          if (!existing) {
            console.warn('[HappyStore] handleToolEnd: No aggregation for session', sessionId);
            return state;
          }

          // Find and update the entry
          const endTimestamp = Date.now();
          const updatedEntries = existing.entries.map((entry) => {
            if (entry.id === toolId) {
              return {
                ...entry,
                status: success ? 'completed' : 'failed',
                completedAt: endTimestamp,
                endTime: endTimestamp, // alias for useLiveDuration hook
                durationMs,
                error: success ? undefined : error,
                output: result?.slice(0, 500), // Truncate output
              } as ToolCallEntry;
            }
            return entry;
          });

          const newRunningCount = existing.runningCount - 1;
          const newCompletedCount = success ? existing.completedCount + 1 : existing.completedCount;
          const newFailedCount = success ? existing.failedCount : existing.failedCount + 1;

          return {
            toolCallAggregations: {
              ...state.toolCallAggregations,
              [sessionId]: {
                ...existing,
                entries: updatedEntries,
                runningCount: Math.max(0, newRunningCount),
                completedCount: newCompletedCount,
                failedCount: newFailedCount,
              },
            },
          };
        });

        console.log('[HappyStore] Tool end:', { sessionId, toolId, success });
      },

      handleAllToolsComplete: (sessionId, reason = 'completed') => {
        set((state) => {
          const existing = state.toolCallAggregations[sessionId];
          if (!existing) {
            console.warn('[HappyStore] handleAllToolsComplete: No aggregation for session', sessionId);
            return state;
          }

          // Mark all running tools as completed/canceled
          const endTimestamp = Date.now();
          const updatedEntries = existing.entries.map((entry) => {
            if (entry.status === 'running') {
              return {
                ...entry,
                status: reason === 'canceled' ? 'failed' : 'completed',
                completedAt: endTimestamp,
                endTime: endTimestamp,
                durationMs: entry.startedAt ? endTimestamp - entry.startedAt : 0,
                error: reason === 'canceled' ? 'Canceled by session stop' : undefined,
              } as ToolCallEntry;
            }
            return entry;
          });

          const runningToolsCompleted = existing.entries.filter(e => e.status === 'running').length;

          return {
            toolCallAggregations: {
              ...state.toolCallAggregations,
              [sessionId]: {
                ...existing,
                entries: updatedEntries,
                runningCount: 0,
                completedCount: reason === 'canceled'
                  ? existing.completedCount
                  : existing.completedCount + runningToolsCompleted,
                failedCount: reason === 'canceled'
                  ? existing.failedCount + runningToolsCompleted
                  : existing.failedCount,
              },
            },
          };
        });

        console.log('[HappyStore] All tools completed:', { sessionId, reason });
      },

      setToolCallAgentInfo: (sessionId, agentInfo) => {
        set((state) => {
          const existing = state.toolCallAggregations[sessionId];
          if (!existing) return state;

          return {
            toolCallAggregations: {
              ...state.toolCallAggregations,
              [sessionId]: {
                ...existing,
                agentInfo,
              },
            },
          };
        });
      },

      markToolCallsCompleted: (sessionId) => {
        set((state) => {
          const existing = state.toolCallAggregations[sessionId];
          if (!existing) return state;

          return {
            toolCallAggregations: {
              ...state.toolCallAggregations,
              [sessionId]: {
                ...existing,
                isCompleted: true,
                runningCount: 0, // Force running count to 0
              },
            },
          };
        });
        console.log('[HappyStore] Tool calls marked completed:', sessionId);
      },

      clearToolCallAggregation: (sessionId) => {
        set((state) => {
          const { [sessionId]: _, ...rest } = state.toolCallAggregations;
          return { toolCallAggregations: rest };
        });
        console.log('[HappyStore] Tool call aggregation cleared:', sessionId);
      },

      getToolCallState: (sessionId) => {
        return get().toolCallAggregations[sessionId];
      },

      // Agent lifecycle events (for LiveAgentCard real-time display)
      handleAgentSpawn: (event) => {
        const { sessionId, agentId, spawnTime, parentAgentId, taskDescription, agentName, estimatedTokens } = event;

        set((state) => {
          const newAgent: ActiveAgentState = {
            id: agentId,
            sessionId,
            type: 'Task',
            status: 'running',
            startTime: spawnTime,
            toolCount: 0,
            tokenCount: estimatedTokens || 0,
            description: taskDescription,
            parentAgentId,
            name: agentName,
          };

          return {
            activeAgents: {
              ...state.activeAgents,
              [sessionId]: newAgent,
            },
          };
        });

        console.log('[HappyStore] Agent spawned:', { sessionId, agentId, agentName, taskDescription });
      },

      handleAgentUpdate: (event) => {
        const { sessionId, agentId, tokensUsed } = event;

        set((state) => {
          const existing = state.activeAgents[sessionId];
          if (!existing || existing.id !== agentId) return state;

          // Also update tool count from tool call aggregation
          const toolAgg = state.toolCallAggregations[sessionId];
          const toolCount = toolAgg?.totalCount || existing.toolCount;

          return {
            activeAgents: {
              ...state.activeAgents,
              [sessionId]: {
                ...existing,
                tokenCount: tokensUsed ?? existing.tokenCount,
                toolCount,
              },
            },
          };
        });
      },

      handleAgentComplete: (event) => {
        const { sessionId, agentId, endTime, success, tokensUsed, error } = event;

        set((state) => {
          const existing = state.activeAgents[sessionId];
          if (!existing || existing.id !== agentId) return state;

          // Get final tool count from aggregation
          const toolAgg = state.toolCallAggregations[sessionId];
          const toolCount = toolAgg?.totalCount || existing.toolCount;

          return {
            activeAgents: {
              ...state.activeAgents,
              [sessionId]: {
                ...existing,
                status: success ? 'done' : 'error',
                endTime,
                tokenCount: tokensUsed,
                toolCount,
              },
            },
          };
        });

        console.log('[HappyStore] Agent complete:', { sessionId, agentId, success, tokensUsed });
      },

      getActiveAgent: (sessionId) => {
        return get().activeAgents[sessionId];
      },

      clearActiveAgent: (sessionId) => {
        set((state) => {
          const { [sessionId]: _, ...rest } = state.activeAgents;
          return { activeAgents: rest };
        });
      },

      // Daemon logs
      addDaemonLog: (log) => {
        set((state) => {
          const MAX_LOGS = 50;
          const newLogs = [...state.daemonLogs, log];
          // Keep only the last MAX_LOGS entries
          if (newLogs.length > MAX_LOGS) {
            return { daemonLogs: newLogs.slice(-MAX_LOGS) };
          }
          return { daemonLogs: newLogs };
        });
      },

      clearDaemonLogs: () => {
        set({ daemonLogs: [] });
      },

      // Reset store
      reset: () => set(initialState),
    })),
    { name: 'happy-store' }
  )
);

// Subscribe to state changes and persist automatically
// Debounce to avoid excessive writes
let saveTimeout: ReturnType<typeof setTimeout> | null = null;
useHappyStore.subscribe(
  (state) => ({
    sessions: state.sessions,
    messagesBySession: state.messagesBySession,
    activeSessionId: state.activeSessionId,
    contextInfo: state.contextInfo,
    sessionHealthBySession: state.sessionHealthBySession,
  }),
  () => {
    // Debounce saves to avoid excessive localStorage writes
    if (saveTimeout) clearTimeout(saveTimeout);
    saveTimeout = setTimeout(() => {
      useHappyStore.getState().saveToStorage();
    }, 500);
  },
  { equalityFn: (a, b) => JSON.stringify(a) === JSON.stringify(b) }
);

/**
 * Selector hooks for specific state slices
 *
 * IMPORTANT: These selectors must return stable references to avoid infinite loops.
 * Use useShallow for object/array results or primitive selectors.
 */

// Stable empty array reference for when there are no messages
const EMPTY_MESSAGES: HappyMessage[] = [];

/** Get the active session - uses useShallow for stable reference */
export const useActiveSession = (): HappySession | null => {
  const { sessions, activeSessionId } = useHappyStore(
    useShallow((state) => ({
      sessions: state.sessions,
      activeSessionId: state.activeSessionId,
    }))
  );
  return sessions.find((s) => s.session_id === activeSessionId) || null;
};

/** Get messages for active session - uses useShallow and stable empty array */
export const useActiveMessages = (): HappyMessage[] => {
  const { messagesBySession, activeSessionId } = useHappyStore(
    useShallow((state) => ({
      messagesBySession: state.messagesBySession,
      activeSessionId: state.activeSessionId,
    }))
  );
  if (!activeSessionId) return EMPTY_MESSAGES;
  return messagesBySession[activeSessionId] || EMPTY_MESSAGES;
};

/** Check if connected - returns primitive boolean */
export const useIsConnected = (): boolean => {
  return useHappyStore((state) => state.connectionState === 'connected');
};

/** Check if authenticated - returns primitive boolean */
export const useIsAuthenticated = (): boolean => {
  return useHappyStore((state) => state.auth.authenticated);
};

// Default healthy session state for when no health record exists
const DEFAULT_HEALTHY_SESSION: SessionHealth = {
  status: 'healthy',
  errorMessage: undefined,
  errorTimestamp: undefined,
};

/** Get the health status of the active session */
export const useActiveSessionHealth = (): SessionHealth => {
  const { sessionHealthBySession, activeSessionId } = useHappyStore(
    useShallow((state) => ({
      sessionHealthBySession: state.sessionHealthBySession,
      activeSessionId: state.activeSessionId,
    }))
  );
  if (!activeSessionId) return DEFAULT_HEALTHY_SESSION;
  return sessionHealthBySession[activeSessionId] || DEFAULT_HEALTHY_SESSION;
};

/** Get the health status of a specific session */
export const useSessionHealth = (sessionId: string): SessionHealth => {
  return useHappyStore(
    (state) => state.sessionHealthBySession[sessionId] || DEFAULT_HEALTHY_SESSION
  );
};

/** Get daemon reconnection notification (returns null if none active) */
export const useDaemonReconnection = (): DaemonReconnection | null => {
  return useHappyStore((state) => state.daemonReconnection);
};

/** Get relay health state for connection status indicator */
export const useRelayHealth = (): RelayHealthState => {
  return useHappyStore((state) => state.relayHealth);
};

// Default empty array for stable daemon list reference
const EMPTY_DAEMONS: RegisteredDaemon[] = [];

/** Get all registered daemons - uses useShallow for stable reference */
export const useDaemons = (): RegisteredDaemon[] => {
  return useHappyStore(useShallow((state) => state.daemons)) || EMPTY_DAEMONS;
};

/** Get the currently selected daemon ID */
export const useSelectedDaemonId = (): string | null => {
  return useHappyStore((state) => state.selectedDaemonId);
};

/** Get the currently selected daemon - uses useShallow for stable reference */
export const useSelectedDaemon = (): RegisteredDaemon | null => {
  const { daemons, selectedDaemonId } = useHappyStore(
    useShallow((state) => ({
      daemons: state.daemons,
      selectedDaemonId: state.selectedDaemonId,
    }))
  );
  if (!selectedDaemonId) return null;
  return daemons.find((d) => d.id === selectedDaemonId) || null;
};

/** Get daemon registry statistics */
export const useDaemonStats = (): DaemonRegistryStats => {
  return useHappyStore((state) => state.daemonStats);
};

/** Check if any daemon is connected */
export const useHasDaemonConnected = (): boolean => {
  return useHappyStore((state) => state.daemons.some((d) => d.status === 'connected'));
};

/** Get connected daemons count */
export const useConnectedDaemonsCount = (): number => {
  return useHappyStore((state) => state.daemons.filter((d) => d.status === 'connected').length);
};

/** Get tool call aggregation state for a session */
export const useToolCallAggregationState = (
  sessionId: string | undefined
): ToolCallAggregationState | undefined => {
  return useHappyStore((state) =>
    sessionId ? state.toolCallAggregations[sessionId] : undefined
  );
};

/** Check if a session has active (running) tool calls */
export const useHasActiveToolCalls = (sessionId: string | undefined): boolean => {
  return useHappyStore((state) => {
    if (!sessionId) return false;
    const agg = state.toolCallAggregations[sessionId];
    return agg ? agg.runningCount > 0 : false;
  });
};

/** Get tool call counts for a session */
export const useToolCallCounts = (
  sessionId: string | undefined
): { total: number; completed: number; running: number; errors: number } => {
  return useHappyStore((state) => {
    if (!sessionId) return { total: 0, completed: 0, running: 0, errors: 0 };
    const agg = state.toolCallAggregations[sessionId];
    if (!agg) return { total: 0, completed: 0, running: 0, errors: 0 };
    return {
      total: agg.entries.length,
      completed: agg.completedCount,
      running: agg.runningCount,
      errors: agg.failedCount,
    };
  });
};

/** Get active agent state for a session (for LiveAgentCard real-time display) */
export const useActiveAgentState = (
  sessionId: string | undefined
): ActiveAgentState | undefined => {
  return useHappyStore((state) =>
    sessionId ? state.activeAgents[sessionId] : undefined
  );
};
